import { Typography } from 'antd';
import { type ParagraphProps } from 'antd/es/typography/Paragraph';
import styled from 'styled-components';

const { Paragraph } = Typography;

const StyledParagraph = styled(Paragraph)`
  &.ant-typography {
    margin-bottom: 0;
  }
  .ant-typography-expand {
    white-space: nowrap;
  }
  .expanded-symbol {
    font-size: 1.2em;
    line-height: 1;
  }
    
`;

export interface TableCellEllipsisProps
  extends Omit<ParagraphProps, 'ellipsis'> {
  rows?: number;
  width?: number;
  tooltip?: boolean;
  expandable?: boolean;
}

const renderExpandSymbol = (expanded: boolean) => (
  <b className="expanded-symbol" aria-hidden="true">
    {expanded ? '−' : '+'}
  </b>
);

/**
 * Componente per visualizzare, all'interno di una cella di una tabella,
 * un testo con ellissi.
 *
 * @remarks
 * **Richiede una larghezza dichiarata sulla colonna ospitante** per funzionare:
 * l'ellipsis multi-row di Antd (`-webkit-line-clamp`) si attiva solo se la
 * cella ha un vincolo dimensionale. Senza, la colonna si dilata sul contenuto
 * e l'ellipsis non scatta mai.
 *
 * Dichiarare uno dei due sulla colonna che usa `<TableCellEllipsis />`:
 * - `width: number` (consigliato, vale sempre)
 * - `widthWithActiveScroll: number` (solo quando la tabella ha `hasFixedColumns`)
 *
 */
export function TableCellEllipsis(props: TableCellEllipsisProps) {
  const {
    rows,
    width,
    tooltip = true,
    expandable = true,
    ...otherProps
  } = props;
  return (
    <div
      style={
        width ? { maxWidth: width, minWidth: width / 2 } : { minWidth: 60 }
      }
    >
      <p className="sr-only">{otherProps.children}</p>
      <StyledParagraph
        ellipsis={{
          rows: rows ?? 2,
          expandable: expandable ? 'collapsible' : false,
          tooltip,
          symbol: renderExpandSymbol,
        }}
        {...otherProps}
        // per lo screen reader nascondiamo l'elemento
        // e mostriamo il testo completo in un paragrafo nascosto sr-only
        aria-hidden="true"
      />
    </div>
  );
}
